import { useTranslation } from "@sagot/i18n";
import { Icon, Progress, cn, type IconName } from "@sagot/ui";
import { formatDate } from "../shared/format";
import { isProcessing } from "../shared/ingestion";
import type { SourceView } from "../shared/types";
import type { SourceEventsState } from "../shared/use-source-events";
import { timelineFor, type StepState } from "./timeline";

const DOT: Record<StepState, { className: string; icon: IconName }> = {
  done: { className: "bg-success text-white dark:text-ink-950", icon: "check" },
  running: { className: "bg-primary text-on-primary", icon: "refresh-cw" },
  failed: { className: "bg-danger-fill text-white", icon: "x" },
  pending: { className: "border border-line-strong bg-surface text-fg-subtle", icon: "circle" },
};

/** Live processing timeline (status + stage via SSE); per-stage timings aren't exposed by the API. */
export function TimelineCard({ source, live }: { source: SourceView; live: SourceEventsState }) {
  const { t, i18n } = useTranslation();
  const k = "deployKnowledge.sourceDetail.timeline";
  const entries = timelineFor(source.status, source.stage);
  const processing = isProcessing(source.status);
  return (
    <section className="card" aria-labelledby="source-timeline-title">
      <div className="card-header">
        <h2 id="source-timeline-title" className="card-title">
          {t(`${k}.title`)}
        </h2>
        <span className="text-xs font-semibold text-fg-subtle">{t(`${k}.total`, { version: source.version })}</span>
      </div>
      <div className="card-body space-y-3">
        {processing ? (
          <div aria-live="polite">
            <Progress value={source.progress} size="sm" label={t(`${k}.progress`, { percent: source.progress })} />
            <p className="mt-1 text-xs text-fg-subtle">
              {t(`${k}.progress`, { percent: source.progress })} · {t(live === "live" ? `${k}.live` : `${k}.polling`)}
            </p>
          </div>
        ) : null}
        <ol>
          {entries.map((entry, index) => {
            const dot = DOT[entry.state];
            const last = index === entries.length - 1;
            const time = index === 0 ? source.created_at : last && entry.state === "done" ? source.updated_at : null;
            return (
              <li key={entry.step} className={cn("relative flex gap-3", !last && "pb-4")}>
                {!last ? <span className="absolute bottom-0 left-[9px] top-5 w-px bg-line-strong" aria-hidden="true" /> : null}
                <span className={cn("relative z-10 inline-flex size-[18px] shrink-0 items-center justify-center rounded-pill", dot.className)} aria-hidden="true">
                  <Icon name={dot.icon} className="size-3" />
                </span>
                <div className="flex flex-1 justify-between gap-2 text-sm">
                  <div>
                    <div className="font-semibold">{t(`${k}.${entry.step}`)}</div>
                    <div className={cn("text-xs", entry.state === "failed" ? "text-danger" : "text-fg-subtle")}>
                      {entry.state === "failed" && source.status_reason ? source.status_reason : t(`${k}.${entry.state}`)}
                    </div>
                  </div>
                  {time ? <div className="shrink-0 whitespace-nowrap text-right text-xs text-fg-muted">{formatDate(time, i18n.language, true)}</div> : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
